import { ICellEditorAngularComp } from '@ag-grid-community/angular';
import { ICellEditorParams } from '@ag-grid-community/core';
import { AfterViewInit, Component, ElementRef, HostListener, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatCalendar, MatDatepicker } from '@angular/material/datepicker';
import { ComponentBaseComponent } from '@app/core/components/component-base/component-base.component';
import { TTDateAdapter } from '@app/core/models/date-adapter';
import { TranslateService } from '@app/core/services/translate.service';
import { UserStore } from '@app/core/services/user.store';
import type { GridRefreshableCellEditor } from '../../helpers/grid-active-editor.helpers';

type DateTimeCellEditorParams = ICellEditorParams & { onCellEditorValueChanged?: () => void };

/**
 * Represents a datetime cell editor component for AG-Grid.
 */
@Component({
    selector: 'tt-date-time-cell-editor',
    templateUrl: './date-time-cell-editor.component.html',
    styleUrls: ['./date-time-cell-editor.component.css'],
    encapsulation: ViewEncapsulation.None,
})
export class DateTimeCellEditorComponent extends ComponentBaseComponent implements ICellEditorAngularComp, AfterViewInit, GridRefreshableCellEditor {
    /**
     * Refernce to the datetime html input element.
     */
    @ViewChild('inputRef')
    public inputRef?: ElementRef;

    @ViewChild('timeRef')
    public timeRef?: ElementRef;

    /**
     * Reference to the date picker component.
     */
    @ViewChild(MatDatepicker) picker?: MatDatepicker<Date>;

    /**
     * Cell editor parameters passed from the cell.
     */
    public params?: DateTimeCellEditorParams;

    public dateFormat?: string;

    /**
     * The date value.
     */
    public value: Date | null = null;

    public time: string = '';

    /**
     * Whether the value in the cell has been edited since editing started.
     */
    public hasBeenEdited: boolean = false;

    public datePlaceholder: string = '';

    public timePlaceholder: string = '--:--';

    @HostListener('keydown', ['$event'])
    public keydown(event: KeyboardEvent) {
        if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            this.params?.api.stopEditing(true);
            this.params?.api.setFocusedCell(this.params.rowIndex, this.params.colDef.field!);
        }
    }

    constructor(
        private userStore: UserStore,
        translate: TranslateService,
    ) {
        super();
        this.userStore.dateFormatChanged.subscribe(async (format) => {
            this.dateFormat = format.replaceAll('m', 'M');
            this.datePlaceholder = await translate.translate(format);
        });
    }

    private markAsEdited(): void {
        this.hasBeenEdited = true;
        this.params?.onCellEditorValueChanged?.();
    }

    /**
     * Sets the date value when changed.
     *
     * @param event string representation of the new date.
     */
    onDateChanged(event: Date | string | null, markAsEdited: boolean = true) {
        if (markAsEdited === true) {
            this.markAsEdited();
        }

        this.value = typeof event === 'string' ? (event ? new Date(event) : null) : event;
    }

    public syncValueFromRow(value: unknown): void {
        this.onDateChanged(value as Date | string | null, false);
        setTimeout(() => {
            this.inputRef?.nativeElement.select();
        }, 1);
    }

    onTimeChanged(_: string, markAsEdited: boolean = true) {
        if (markAsEdited === true) {
            this.markAsEdited();
        }
    }

    async onDateKeydown(event: KeyboardEvent) {
        const dateField = this.inputRef?.nativeElement as HTMLInputElement;
        const timeField = this.timeRef?.nativeElement as HTMLInputElement;

        if (event.key === ' ') {
            const oldValue = this.time;

            timeField?.focus(); // this makes the time get replaced with the space

            setTimeout(() => {
                this.time = oldValue; // so i reset the value here
                setTimeout(() => {
                    timeField?.select(); // but i need to wait for the change to finish before i can select the value (thus many timeouts).
                });
            });
        } else if (event.key === 'ArrowRight' && (await this.selectionTwiceInLastPosition(dateField))) {
            timeField?.focus();
            timeField?.setSelectionRange(0, 0, 'forward'); // to prevent jumping of selection to end of field.
        }
    }

    async onTimeKeydown(event: KeyboardEvent) {
        if (event.key === ' ') {
        } else if (event.key === 'ArrowLeft' || event.key === 'Backspace' || event.key === 'Delete') {
            const timeField = this.timeRef?.nativeElement as HTMLInputElement;

            if (await this.selectionTwiceInFirstPosition(timeField)) {
                const dateField = this.inputRef?.nativeElement as HTMLInputElement;
                dateField?.focus();
                dateField?.setSelectionRange(dateField.value.length, dateField.value.length, 'backward');
                this.time = this.getTimeValue(this.time);
            }
        }
    }

    /**
     * Checks whether the given input field has had a selection (*arrow navigation*) to the end of the input field twice in a row.
     * Returns a promise containing `true` if the field had selection twice in a row, `false` if not.
     *
     * @param inputElement the input field to check if had selection to the end of the input field twice in a row.
     * @returns a promise containing `true` if selection to the end of the input field happened twice in a row, `false` if not.
     */
    private selectionTwiceInLastPosition(inputElement: HTMLInputElement): Promise<boolean> {
        return new Promise((resolve) => {
            if (!!inputElement && inputElement.tagName === 'INPUT') {
                const previousSelectionEnd = inputElement.selectionEnd;

                setTimeout(() => {
                    if (inputElement?.selectionEnd === inputElement.value.length && previousSelectionEnd === inputElement.value.length) {
                        resolve(true);
                    } else {
                        resolve(false);
                    }
                });
            } else {
                resolve(false);
            }
        });
    }

    /**
     * Checks whether the given input field has had a selection (*arrow navigation*) to the start of the input field twice in a row.
     * Returns a promise containing `true` if the field had selection twice in a row, `false` if not.
     *
     * @param inputElement the input field to check if had selection to the start of the input field twice in a row.
     * @returns a promise containing `true` if selection to the start of the input field happened twice in a row, `false` if not.
     */
    private selectionTwiceInFirstPosition(inputElement: HTMLInputElement): Promise<boolean> {
        return new Promise((resolve) => {
            if (!!inputElement && inputElement.tagName === 'INPUT') {
                const previousSelectionStart = inputElement.selectionStart;

                setTimeout(() => {
                    if (inputElement?.selectionStart === 0 && previousSelectionStart === 0) {
                        resolve(true);
                    } else {
                        resolve(false);
                    }
                });
            } else {
                resolve(false);
            }
        });
    }

    agInit(params: DateTimeCellEditorParams): void {
        this.params = params;

        this.value = !!this.params.data[this.params.column.getColId()] ? new Date(this.params.data[this.params.column.getColId()]) : null;
        this.time = this.getTimeValue(this.params.data[this.params.column.getColId()]);
    }

    private getTimeValue(value?: string | Date | null): string {
        if (!value) {
            return '';
        } else if (value instanceof Date && value.toString() === 'Invalid Date') {
            return '00:00';
        } else if (value instanceof Date) {
            return `${this.getTwoDigitNumberString(value.getHours())}:${this.getTwoDigitNumberString(value.getMinutes())}`;
        } else if (typeof value === 'string') {
            if (this.isISODateTimeString(value)) {
                return value.split('T')[1].substring(0, 5);
            } else if (this.isDateTimeString(value)) {
                return value.split(' ')[1].substring(0, 5);
            } else if (this.containsOnlyDigits(value)) {
                return this.getTimeFromDigitString(value);
            } else {
                return this.parsedTime(value);
            }
        }

        return '00:00';
    }

    private getTimeFromDigitString(string: string) {
        if (string.length === 3) {
            return '0' + string[0] + ':' + string[1] + string[2];
        } else if (string.length === 4) {
            return string[0] + string[1] + ':' + string[2] + string[3];
        } else {
            return '00:00';
        }
    }

    private getTwoDigitNumberString(number: number) {
        return number < 10 ? '0' + number : number;
    }

    private isISODateTimeString(string: string) {
        return string.includes('T') && !!string.split('T')[1]?.substring(0, 5) && this.validTimeFormat(string.split('T')[1].substring(0, 5));
    }

    private isDateTimeString(string: string) {
        return string.includes(' ') && !!string.split(' ')[1]?.substring(0, 5) && this.validTimeFormat(string.split(' ')[1].substring(0, 5));
    }

    private containsOnlyDigits(string: string) {
        return /^\d+$/.test(string);
    }

    private validTimeFormat(time: string) {
        return /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(time);
    }

    private parsedTime(time: string) {
        const seperator = time.match(/\D/)?.[0] ?? '';

        let hour = Number(time.split(seperator)[0].replace(/\D+/g, ''));
        let minute = Number(time.split(seperator)[1].replace(/\D+/g, ''));

        if (isNaN(hour) || hour < 0 || hour > 24 || isNaN(minute) || minute < 0 || minute > 60) {
            return '00:00';
        } else {
            return this.getTwoDigitNumberString(hour) + ':' + this.getTwoDigitNumberString(minute);
        }
    }

    getValue() {
        this.time = this.getTimeValue(this.time);
        this.value?.setHours(+(this.time.split(':')[0] ?? '00'), +(this.time.split(':')[1] ?? '00'));

        if (this.value?.toString() === 'Invalid Date') {
            return this.params?.node.data[this.params.column.getColId()] ?? null;
        } else {
            return this.value;
        }
    }

    ngAfterViewInit() {
        window.setTimeout(() => {
            if (!!this.params && this.inputRef && /^\d+$/.test(this.params.eventKey || '')) {
                this.inputRef.nativeElement.value = this.params.eventKey;
                this.inputRef?.nativeElement.focus();
            } else {
                this.inputRef?.nativeElement.focus();
                this.inputRef?.nativeElement.select();
            }
        });
    }

    onCalendarOpened() {
        setTimeout(() => {
            const calendar = (this.picker as any)._componentRef.instance._calendar;
            if (!calendar) return;

            this.appendWeekNumbers(calendar, true);

            // Trigger når man bytter måned/år med pilene
            calendar.stateChanges.subscribe(() => {
                setTimeout(() => this.appendWeekNumbers(calendar), 0);
            });

            // Trigger når man bytter mellom dato-visning og måned-valg-visning
            calendar.viewChanged.subscribe(() => {
                setTimeout(() => this.appendWeekNumbers(calendar), 0);
            });
        });
    }

    private getIsoWeek(date: Date): number {
        const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        const dayNum = d.getUTCDay() || 7;
        d.setUTCDate(d.getUTCDate() + 4 - dayNum);
        const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
        return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
    }

    private ensureWrapperExists(datepickerContent: HTMLElement) {
        let wrapper = document.getElementById('week-number-wrapper');

        if (!wrapper && datepickerContent) {
            wrapper = document.createElement('div');
            wrapper.id = 'week-number-wrapper';

            datepickerContent.parentNode?.insertBefore(wrapper, datepickerContent);

            const weekContent = document.createElement('div');
            weekContent.id = 'week-number-content';

            const table = document.createElement('table');
            table.id = 'week-number-table';

            weekContent.appendChild(table);
            wrapper.appendChild(weekContent);
            wrapper.appendChild(datepickerContent);
        }
        return wrapper;
    }

    private async appendWeekNumbers(calendar: MatCalendar<any>, firstOpened: boolean = false) {
        const dateAdapter = (calendar as any)._dateAdapter;
        const datepickerContent = document.querySelector('.mat-datepicker-content') as HTMLElement;
        if (!datepickerContent) return;

        this.ensureWrapperExists(datepickerContent);

        const table = document.getElementById('week-number-table');
        const weekContent = document.getElementById('week-number-content');
        if (!table || !weekContent) return;

        // 1. Sjekk visning. Hvis ikke 'month', deaktiver kolonnen
        if (calendar.currentView !== 'month') {
            weekContent.classList.add('is-hidden');
            table.innerHTML = '';
            return;
        }

        // 2. Aktiver kolonnen for månedsvisning
        weekContent.classList.remove('is-hidden');

        const calendarRows = document.querySelectorAll('.mat-calendar-body tr');
        let rowsHtml = '';

        calendarRows.forEach((row) => {
            const cells = row.querySelectorAll('.mat-calendar-body-cell');
            let weekNum: number | string = '';
            const rowHeight = (row as HTMLElement).offsetHeight || 40;

            for (let i = 0; i < cells.length; i++) {
                const cellText = (cells[i] as HTMLElement).innerText.trim();
                const dayValue = parseInt(cellText, 10);

                if (!isNaN(dayValue)) {
                    const dateInRow = dateAdapter.createDate(dateAdapter.getYear(calendar.activeDate), dateAdapter.getMonth(calendar.activeDate), dayValue);
                    weekNum = this.getIsoWeek(dateInRow);
                    break;
                }
            }
            rowsHtml += `<tr><td style="height: ${rowHeight}px;">${weekNum}</td></tr>`;
        });

        if (firstOpened) {
            await new Promise<void>((resolve) => setTimeout(() => resolve(), 150));
        }

        // 3. Juster posisjon basert på header-høyde
        const calendarBody = document.querySelector('.mat-calendar-body');
        if (calendarBody) {
            const bodyTop = calendarBody.getBoundingClientRect().top;
            const contentTop = datepickerContent.getBoundingClientRect().top;
            weekContent.style.paddingTop = `${bodyTop - contentTop}px`;
        }

        table.innerHTML = rowsHtml;
    }
}
